import * as React from 'react';
import { useEffect, useState } from 'react';
import useCollapse from 'react-collapsed';
import { UseCollapseOutput } from 'react-collapsed/dist/types';
import { TransProps, useTranslation } from 'react-i18next';

import { Color, IconName } from '../../enums';
import { UseState } from '../../types';
import { Checkbox } from '../checkbox/checkbox.component';
import { Icon } from '../icon/icon.component';
import { RadioButton } from '../radioButton/radioButton.component';

import { 
  ChildrenContainer,  
  ChooseBoxContainer, 
  CollapsedChildrenWrapper, 
  Container, 
  DetailsContainer, 
  IconButton, 
  ImageContainer, 
  Logo, 
  RadioContainer, 
  SubTitle, 
  Title, 
  TitlesContainer, 
  UnderlineButton 
} from './chooseBox.styled';
import { ChooseBoxProps } from './chooseBox.types';

export const ChooseBox: React.FC<ChooseBoxProps> = (props: ChooseBoxProps): JSX.Element => {
  const { 
    title, 
    onChange, 
    isActive = false, 
    subtitle, 
    icon, 
    iconSize, 
    logo, 
    onRemove, 
    children, 
    isCheckbox,
    isChangeButtonVisible,
    className 
  }: ChooseBoxProps = props;
  const { t }: TransProps<never> = useTranslation();
  const [isChecked, setIsChecked]: UseState<boolean> = useState<boolean>(isActive);
  const { getCollapseProps, setExpanded, isExpanded }: UseCollapseOutput = useCollapse();

  const onChangeAction: () => void = (): void => {
    if (isCheckbox) {
      if (onChange) {
        onChange(!isChecked);
      }
      setIsChecked(!isChecked);
    } else if (!isExpanded && !isChecked) {
      if (onChange) {
        onChange(true);
      }
      setIsChecked(true);
      if (!isChangeButtonVisible) {
        setExpanded(true);
      }
    }
  };

  const onCheckboxChangeAction: (value: boolean) => void = (value: boolean): void => {
    setIsChecked(value);
  };

  const openChildrenContainer: () => void = (): void => {
    if (onChange) {
      onChange(true);
    }
    setExpanded(true);
    setIsChecked(true);
  };

  useEffect(
    (): void => {
      setIsChecked(isActive);
      setExpanded(isActive);
    },
    [isActive]
  );

  return (
    <Container className={className}>
      <ChooseBoxContainer $isActive={isChecked} onClick={onChangeAction}>
        {(!!logo || !!icon) ? (
          <DetailsContainer>
            <ImageContainer>
              {!!logo && <Logo src={logo} alt={title} />}
              {!!icon && !logo && <Icon color={Color.Error500} name={icon} size={iconSize ?? 24} />}
            </ImageContainer>
            <TitlesContainer>
              <Title>{title}</Title>
              {!!subtitle && <SubTitle>{subtitle}</SubTitle>}
            </TitlesContainer>
          </DetailsContainer>
        ) : isCheckbox ? (
          <RadioContainer>
            <Checkbox color={Color.Error500} checked={isChecked} onChange={onCheckboxChangeAction} />
            <Title>{title}</Title>
          </RadioContainer>
        ) : (
          <RadioContainer>
            <RadioButton label={title} isActive={isChecked} />
          </RadioContainer>
        )}
        {!!onRemove && (
          <IconButton onClick={onRemove} data-testid='choose-box-remove-button'>
            <Icon name={IconName.Delete} size={18} />
          </IconButton>
        )}
        {isChangeButtonVisible && !!children && !isExpanded && (
          <UnderlineButton onClick={openChildrenContainer}>
            {t('onarte.ui.chooseBox.underlineButton.label')}
          </UnderlineButton>
        )}
      </ChooseBoxContainer>
      {!!children && isExpanded && (
        <CollapsedChildrenWrapper {...getCollapseProps()}> 
          <ChildrenContainer $isActive={isExpanded}>
            {children}
          </ChildrenContainer>
        </CollapsedChildrenWrapper>
      )}
    </Container>
  );
};
